import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShipmentFields, emptyShipment, toPayload } from "@/components/ShipmentFields";
import { useApi, useMeta, errMsg } from "@/lib/api";

function Estimate({ form }) {
  const { data: meta } = useMeta();
  const rate = meta?.services?.[form.package.service] ?? 8;
  const w = Number(form.package.weight_kg) || 0;
  const q = Number(form.package.quantity) || 1;
  const total = w ? 15 + rate * w * q : 0;
  return (
    <div className="bg-[#0A192F] text-white rounded-sm p-6 space-y-4 lg:sticky lg:top-24" data-testid="cost-estimate">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Estimated cost</p>
      <p className="font-mono text-4xl font-bold text-[#FF6B00]" data-testid="cost-estimate-total">${total.toFixed(2)}</p>
      <div className="text-sm text-slate-300 space-y-1">
        <p>Base handling: $15.00</p>
        <p>{form.package.service}: ${rate.toFixed(2)} / kg</p>
        <p>{w || 0} kg × {q}</p>
      </div>
      <p className="text-xs text-slate-400">Estimate only. Final price is confirmed when we approve your booking.</p>
    </div>
  );
}

function Success({ s, onAgain }) {
  return (
    <div className="max-w-xl mx-auto text-center bg-white border border-slate-200 rounded-sm p-10 space-y-4" data-testid="booking-success">
      <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
      <h2 className="text-2xl font-bold text-[#0A192F]">Booking request received</h2>
      <p className="text-slate-600 text-sm">Your tracking number is below. Our team will review and approve your shipment shortly.</p>
      <p className="font-mono font-bold tracking-wider text-2xl text-[#FF6B00]" data-testid="booking-tracking-number">{s.tracking_number}</p>
      <div className="flex flex-wrap justify-center gap-3 pt-2">
        <Link to={`/track/${s.tracking_number}`} className="px-5 py-2.5 bg-[#0A192F] text-white text-sm font-semibold rounded-sm" data-testid="booking-track-link">Track it</Link>
        <button onClick={onAgain} className="px-5 py-2.5 border border-slate-300 text-sm font-semibold rounded-sm" data-testid="booking-new-btn">Book another</button>
      </div>
    </div>
  );
}

export default function BookPage() {
  const api = useApi();
  const [form, setForm] = useState(emptyShipment());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const s = await api("post", "/bookings", toPayload(form));
      setDone(s);
      toast.success(s.email === "sent" ? "Shipment booked. Confirmation email sent." : "Shipment booked");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <div className="px-4 py-16"><Success s={done} onAgain={() => { setDone(null); setForm(emptyShipment()); }} /></div>;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10 space-y-2">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">Book a shipment</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F]">Request a new consignment</h1>
      </div>
      <form onSubmit={submit} className="grid lg:grid-cols-[2fr_1fr] gap-8 items-start" data-testid="booking-form">
        <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8 space-y-8">
          <ShipmentFields form={form} setForm={setForm} />
          <Button type="submit" disabled={busy} className="w-full sm:w-auto bg-[#FF6B00] hover:bg-[#E05D00] h-12 px-8" data-testid="booking-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Submit booking
          </Button>
        </div>
        <Estimate form={form} />
      </form>
    </div>
  );
}
